import { request } from './api';

type ImageUpdate = { productId: string; imageUrl: string | null };
const channelName = 'longjie-product-images';
const storageKey = 'longjie-product-image-change';
const listeners = new Map<string, Set<() => void>>();
const images = new Map<string, string | null>();
const revisions = new Map<string, number>();
let interval: ReturnType<typeof setInterval> | undefined;
let scheduled: ReturnType<typeof setTimeout> | undefined;
let channel: BroadcastChannel | undefined;
let controller: AbortController | undefined;

function apply(update: ImageUpdate) {
  revisions.set(update.productId, (revisions.get(update.productId) ?? 0) + 1);
  if (images.has(update.productId) && images.get(update.productId) === update.imageUrl) return;
  images.set(update.productId, update.imageUrl);
  listeners.get(update.productId)?.forEach((listener) => listener());
}

function receive(value: unknown) {
  if (!value || typeof value !== 'object') return;
  const update = value as ImageUpdate;
  if (typeof update.productId !== 'string' || !(update.imageUrl === null || typeof update.imageUrl === 'string')) return;
  if (listeners.has(update.productId)) apply(update);
}

function onStorage(event: StorageEvent) {
  if (event.key !== storageKey || !event.newValue) return;
  try { receive(JSON.parse(event.newValue)); } catch { /* An unrelated malformed event is ignored. */ }
}

/** Fetch only mounted images, so refreshing pictures never resets searches or order quantities. */
async function refresh() {
  if (document.visibilityState === 'hidden' || controller || !listeners.size) return;
  const current = new AbortController();
  controller = current;
  const ids = [...listeners.keys()];
  try {
    for (const kind of ['product', 'category'] as const) {
      const matchingIds = ids.filter((id) => id.startsWith('category:') === (kind === 'category'));
      for (let offset = 0; offset < matchingIds.length; offset += 100) {
        const batch = matchingIds.slice(offset, offset + 100);
        const before = new Map(batch.map((id) => [id, revisions.get(id) ?? 0]));
        let updates: ImageUpdate[];
        if (kind === 'category') {
          const params = new URLSearchParams({ prefixes: batch.map((id) => id.slice('category:'.length)).join(',') });
          const result = await request<{ images: Array<{ prefix: string; imageUrl: string | null }> }>(`/api/v1/category-images?${params}`, { signal: current.signal });
          updates = result.images.map((entry) => ({ productId: categoryImageKey(entry.prefix), imageUrl: entry.imageUrl }));
        } else {
          const params = new URLSearchParams({ ids: batch.join(',') });
          const result = await request<{ images: ImageUpdate[] }>(`/api/v1/product-images?${params}`, { signal: current.signal });
          updates = result.images;
        }
        if (current.signal.aborted) return;
        for (const update of updates) {
          // An upload can finish while this request still contains the older URL.
          if (listeners.has(update.productId) && before.get(update.productId) === (revisions.get(update.productId) ?? 0)) apply(update);
        }
      }
    }
  } catch { /* Keep the last image through temporary network/auth failures. */ }
  finally { if (controller === current) controller = undefined; }
}

function refreshVisible() { void refresh(); }

export function scheduleProductImageRefresh() {
  if (scheduled !== undefined) clearTimeout(scheduled);
  scheduled = setTimeout(() => { scheduled = undefined; void refresh(); }, 100);
}

function start() {
  if (interval !== undefined) return;
  interval = setInterval(refreshVisible, 30_000);
  window.addEventListener('focus', refreshVisible);
  document.addEventListener('visibilitychange', refreshVisible);
  window.addEventListener('storage', onStorage);
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(channelName);
    channel.onmessage = (event) => receive(event.data);
  }
}

export function subscribeProductImage(productId: string, listener: () => void) {
  const productListeners = listeners.get(productId) ?? new Set<() => void>();
  productListeners.add(listener);
  listeners.set(productId, productListeners);
  start();
  scheduleProductImageRefresh();
  return () => {
    productListeners.delete(listener);
    if (!productListeners.size) {
      listeners.delete(productId);
    }
    if (listeners.size) return;
    clearInterval(interval);
    clearTimeout(scheduled);
    interval = undefined;
    scheduled = undefined;
    controller?.abort();
    controller = undefined;
    channel?.close();
    channel = undefined;
    // A product can disappear during page navigation while another image keeps
    // an older request alive. Retain its version until the whole view is gone.
    images.clear();
    revisions.clear();
    window.removeEventListener('focus', refreshVisible);
    document.removeEventListener('visibilitychange', refreshVisible);
    window.removeEventListener('storage', onStorage);
  };
}

export function currentProductImage(productId: string) { return images.get(productId); }

/** Called only after the server confirms a saved/deleted image. */
export function publishProductImage(update: ImageUpdate) {
  if (listeners.has(update.productId)) apply(update);
  const publisher = channel ?? (typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(channelName) : undefined);
  publisher?.postMessage(update);
  if (publisher !== channel) publisher?.close();
  try { localStorage.setItem(storageKey, JSON.stringify({ ...update, nonce: crypto.randomUUID() })); }
  catch { /* BroadcastChannel and polling also work when storage is unavailable. */ }
}

export function categoryImageKey(prefix: string) { return `category:${prefix}`; }
export function publishCategoryImage({ prefix, imageUrl }: { prefix: string; imageUrl: string | null }) {
  publishProductImage({ productId: categoryImageKey(prefix), imageUrl });
}
